import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { getUserProfileByUsername, getDetailedGameHistory } from '../services/firebase/users';
import Layout from '../components/Layout';
import { CHATTERS_ID } from '../constants/game';

const DATE_FORMAT = new Intl.DateTimeFormat(undefined, {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

function toDate(ts) {
  if (!ts) return new Date(0);
  return ts.toDate ? ts.toDate() : new Date(ts);
}

function buildStatRows(games, viewerUid) {
  const rows = [];
  for (const game of games) {
    const { playerIds = [], playerNames = {}, rounds = [], completedAt, gameName } = game;
    const gameDate = toDate(completedAt);
    const playerList = playerIds
      .filter((uid) => uid !== CHATTERS_ID)
      .map((uid) => playerNames[uid] ?? uid)
      .join(', ');
    const gameLabel = gameName || `${DATE_FORMAT.format(gameDate)} · ${playerList}`;

    for (const round of [...rounds].sort((a, b) => a.roundNumber - b.roundNumber)) {
      const { roundNumber, song = {}, scores = {}, winners = [] } = round;
      const otherUids = playerIds.filter((uid) => uid !== viewerUid && uid !== CHATTERS_ID);
      const myScoreData = scores[viewerUid];
      const myActualScore = myScoreData?.actualScore ?? null;
      const myPredictions = myScoreData?.predictions ?? {};

      const otherScores = otherUids
        .map((uid) => scores[uid]?.actualScore)
        .filter((v) => v != null);
      const avgOthersScore =
        otherScores.length > 0
          ? otherScores.reduce((a, b) => a + b, 0) / otherScores.length
          : null;

      const myPredErrors = otherUids
        .filter((uid) => myPredictions[uid] != null && scores[uid]?.actualScore != null)
        .map((uid) => Math.abs(myPredictions[uid] - scores[uid].actualScore));
      const myPredAccuracy =
        myPredErrors.length > 0
          ? myPredErrors.reduce((a, b) => a + b, 0) / myPredErrors.length
          : null;

      const inboundErrors = otherUids
        .filter(
          (uid) =>
            scores[uid]?.predictions?.[viewerUid] != null && myActualScore != null
        )
        .map((uid) => Math.abs(scores[uid].predictions[viewerUid] - myActualScore));
      const othersPredAccuracy =
        inboundErrors.length > 0
          ? inboundErrors.reduce((a, b) => a + b, 0) / inboundErrors.length
          : null;

      rows.push({
        gameId: game.id,
        gameLabel,
        completedAt: gameDate,
        roundNumber,
        songTitle: song.title ?? '',
        artist: song.artist ?? '',
        myScore: myActualScore,
        avgOthersScore,
        myPredAccuracy,
        othersPredAccuracy,
        isRoundWinner: (winners ?? []).includes(viewerUid),
      });
    }
  }
  return rows;
}

function exportToCSV(rows, username) {
  const headers = [
    'Game',
    'Round #',
    'Song Title',
    'Artist',
    'My Score',
    "Avg Others' Score",
    'My Prediction Accuracy',
    "Others' Prediction Accuracy For Me",
    'Round Winner',
  ];
  const esc = (v) => {
    const s = v == null ? '' : String(v);
    return s.includes(',') || s.includes('"') || s.includes('\n')
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };
  const csvRows = [
    headers.join(','),
    ...rows.map((r) =>
      [
        esc(r.gameLabel),
        r.roundNumber,
        esc(r.songTitle),
        esc(r.artist),
        r.myScore ?? '',
        r.avgOthersScore != null ? r.avgOthersScore.toFixed(2) : '',
        r.myPredAccuracy != null ? r.myPredAccuracy.toFixed(2) : '',
        r.othersPredAccuracy != null ? r.othersPredAccuracy.toFixed(2) : '',
        r.isRoundWinner ? 'Yes' : 'No',
      ].join(',')
    ),
  ];
  const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${username}-stats.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function SortableHeader({ columnKey, label, sortConfig, onSort, align = 'left' }) {
  const isActive = sortConfig.key === columnKey;
  const direction = isActive ? sortConfig.direction : null;
  const alignClass =
    align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  return (
    <th
      scope="col"
      aria-sort={isActive ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={`text-xs font-medium t-text-disabled uppercase tracking-wider px-3 py-2.5 whitespace-nowrap ${alignClass}`}
    >
      <button
        type="button"
        onClick={() => onSort(columnKey)}
        className={`inline-flex items-center gap-1 t-focus-ring rounded hover:opacity-80 transition-opacity ${align === 'right' ? 'flex-row-reverse' : ''}`}
      >
        <span>{label}</span>
        <span aria-hidden="true" className="opacity-40">
          {isActive ? (direction === 'asc' ? '▲' : '▼') : '⇅'}
        </span>
      </button>
    </th>
  );
}

export default function StatsPage() {
  const { t } = useTranslation();
  const { username } = useParams();
  const navigate = useNavigate();
  const { user, profile: ownProfile } = useAuth();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [gameHistory, setGameHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'completedAt', direction: 'desc' });

  const isOwnProfile = user && ownProfile?.usernameLower === username.toLowerCase();
  const canSeeDetails = isOwnProfile || (profile?.profileVisible ?? false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setNotFound(false);
      const p = await getUserProfileByUsername(username);
      if (cancelled) return;
      if (!p) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      setProfile(p);
      setLoading(false);
      const isOwn = user && ownProfile?.usernameLower === username.toLowerCase();
      if (!isOwn && !(p.profileVisible ?? false)) {
        navigate(`/profile/${username}`, { replace: true });
      }
    }
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);

  useEffect(() => {
    if (!profile?.id || !canSeeDetails) return;
    let cancelled = false;
    async function loadHistory() {
      setHistoryLoading(true);
      const history = await getDetailedGameHistory(profile.id);
      if (!cancelled) {
        setGameHistory(history);
        setHistoryLoading(false);
      }
    }
    loadHistory();
    return () => {
      cancelled = true;
    };
  }, [profile?.id, canSeeDetails]);

  const rows = useMemo(() => {
    if (!user) return [];
    return buildStatRows(gameHistory, user.uid);
  }, [gameHistory, user]);

  const sortedRows = useMemo(() => {
    const { key, direction } = sortConfig;
    return [...rows].sort((a, b) => {
      const aVal = a[key];
      const bVal = b[key];
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [rows, sortConfig]);

  function handleSort(key) {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc',
    }));
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex-1 flex items-center justify-center t-text-muted">
          {t('stats.loading')}
        </div>
      </Layout>
    );
  }

  if (notFound) {
    return (
      <Layout>
        <div className="flex-1 flex flex-col items-center justify-center gap-3 t-text-muted">
          <p>{t('profile.notFound')}</p>
          <Link to="/lobby" className="text-sm t-text-accent hover:opacity-80 t-focus-ring rounded">
            {t('profile.backToLobby')}
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex-1 flex flex-col items-center px-4 py-8">
        <div className="w-full max-w-5xl space-y-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <Link
                to={`/profile/${username}`}
                className="text-sm t-text-muted hover:opacity-80 transition-opacity t-focus-ring rounded"
              >
                ← {t('stats.backToProfile')}
              </Link>
              <h1 className="text-xl font-bold t-text mt-1">
                {t('stats.pageTitle')} — {profile?.username}
              </h1>
            </div>
            {rows.length > 0 && (
              <button
                type="button"
                onClick={() => exportToCSV(rows, profile?.username ?? username)}
                className="t-border border rounded-xl px-4 py-2 text-sm font-medium t-text hover:opacity-80 transition-opacity t-focus-ring"
              >
                {t('stats.exportCsv')}
              </button>
            )}
          </div>

          {historyLoading ? (
            <div className="t-text-muted text-sm">{t('stats.loading')}</div>
          ) : rows.length === 0 ? (
            <div className="t-bg-surface t-border border rounded-2xl px-5 py-6 text-center">
              <p className="t-text-disabled">{t('stats.noRoundsYet')}</p>
            </div>
          ) : (
            <div className="t-border border rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <caption className="sr-only">
                    {t('stats.tableCaption', { username: profile?.username })}
                  </caption>
                  <thead className="t-bg-surface">
                    <tr className="t-border border-b">
                      <SortableHeader
                        columnKey="gameLabel"
                        label={t('stats.colGame')}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                      />
                      <SortableHeader
                        columnKey="roundNumber"
                        label={t('stats.colRound')}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        align="center"
                      />
                      <SortableHeader
                        columnKey="songTitle"
                        label={t('stats.colSong')}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                      />
                      <SortableHeader
                        columnKey="artist"
                        label={t('stats.colArtist')}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                      />
                      <SortableHeader
                        columnKey="myScore"
                        label={t('stats.colMyScore')}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        align="center"
                      />
                      <SortableHeader
                        columnKey="avgOthersScore"
                        label={t('stats.colAvgOthers')}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        align="center"
                      />
                      <SortableHeader
                        columnKey="myPredAccuracy"
                        label={t('stats.colMyAccuracy')}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        align="center"
                      />
                      <SortableHeader
                        columnKey="othersPredAccuracy"
                        label={t('stats.colOthersAccuracy')}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        align="center"
                      />
                      <SortableHeader
                        columnKey="isRoundWinner"
                        label={t('stats.colWinner')}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        align="center"
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.map((row, i) => (
                      <tr
                        key={`${row.gameId}-${row.roundNumber}-${i}`}
                        className={`t-border border-b last:border-0 ${row.isRoundWinner ? 't-bg-accent-muted' : ''}`}
                      >
                        <td className="px-3 py-3 t-text-secondary max-w-[180px]">
                          <span className="block truncate" title={row.gameLabel}>
                            {row.gameLabel}
                          </span>
                        </td>
                        <td className="px-3 py-3 t-text text-center font-medium">
                          {row.roundNumber}
                        </td>
                        <td className="px-3 py-3 t-text max-w-[140px]">
                          <span className="block truncate" title={row.songTitle}>
                            {row.songTitle || '—'}
                          </span>
                        </td>
                        <td className="px-3 py-3 t-text-secondary max-w-[120px]">
                          <span className="block truncate" title={row.artist}>
                            {row.artist || '—'}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-center font-bold t-text">
                          {row.myScore ?? '—'}
                        </td>
                        <td className="px-3 py-3 text-center t-text-secondary">
                          {row.avgOthersScore != null ? row.avgOthersScore.toFixed(1) : '—'}
                        </td>
                        <td className="px-3 py-3 text-center t-text-muted">
                          {row.myPredAccuracy != null ? row.myPredAccuracy.toFixed(2) : '—'}
                        </td>
                        <td className="px-3 py-3 text-center t-text-muted">
                          {row.othersPredAccuracy != null
                            ? row.othersPredAccuracy.toFixed(2)
                            : '—'}
                        </td>
                        <td className="px-3 py-3 text-center">
                          {row.isRoundWinner ? (
                            <span className="t-text-accent font-bold" aria-label="winner">
                              🏆
                            </span>
                          ) : (
                            <span className="t-text-disabled" aria-hidden="true">
                              —
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
